import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, CalendarClock, Save } from "lucide-react";
import { api, apiError } from "@/lib/api";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const AUD = [["customer", "Customers"], ["restaurant", "Restaurant Partners"], ["delivery", "Delivery Partners"]];

export default function AdminCommunications() {
  const [cfg, setCfg] = useState(null);
  const [runs, setRuns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const load = () => api.get("/admin/communications/weekly").then((r) => { setCfg(r.data.config); setRuns(r.data.recent_runs || []); }).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-coral" /></div>;

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const toggleAud = (a) => set("audiences", cfg.audiences.includes(a) ? cfg.audiences.filter((x) => x !== a) : [...cfg.audiences, a]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/communications/weekly", {
        enabled: cfg.enabled, day: cfg.day, hour: cfg.hour, minute: cfg.minute,
        audiences: cfg.audiences, headline: cfg.headline, custom_note: cfg.custom_note,
      });
      setCfg(data); toast.success("Weekly update settings saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const triggerNow = async () => {
    setTriggering(true);
    try { await api.post("/admin/communications/weekly/trigger"); toast.success("Weekly update dispatch started"); setTimeout(load, 2500); }
    catch (e) { toast.error(apiError(e)); } finally { setTriggering(false); }
  };

  return (
    <div data-testid="admin-communications">
      <div className="bg-white rounded-2xl border border-border p-4">
        <div className="flex items-center gap-2 mb-1"><CalendarClock className="text-coral" size={20} /><h2 className="font-display font-bold text-lg">Weekly Updates</h2></div>
        <p className="text-sm text-ink-600 mb-4">Automatic BiteGoFood weekly updates run on a schedule (email via Resend + in-app notifications). No manual sending required.</p>

        <label className="flex items-center justify-between bg-surface rounded-xl px-4 py-3 mb-3">
          <span className="font-semibold text-sm">Automatic weekly updates</span>
          <input type="checkbox" checked={cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} className="accent-coral w-5 h-5" data-testid="weekly-enabled" />
        </label>

        <div className="grid grid-cols-3 gap-2 mb-3">
          <div><label className="text-xs font-semibold text-ink-600">Day</label><select value={cfg.day} onChange={(e) => set("day", Number(e.target.value))} className="w-full mt-1 bg-surface rounded-lg px-3 py-2 text-sm" data-testid="weekly-day">{DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}</select></div>
          <div><label className="text-xs font-semibold text-ink-600">Hour (IST)</label><input type="number" min={0} max={23} value={cfg.hour} onChange={(e) => set("hour", Number(e.target.value))} className="w-full mt-1 bg-surface rounded-lg px-3 py-2 text-sm" data-testid="weekly-hour" /></div>
          <div><label className="text-xs font-semibold text-ink-600">Minute</label><input type="number" min={0} max={59} value={cfg.minute} onChange={(e) => set("minute", Number(e.target.value))} className="w-full mt-1 bg-surface rounded-lg px-3 py-2 text-sm" /></div>
        </div>
        <p className="text-[11px] text-ink-600 -mt-1 mb-3">Currently scheduled: every <b>{DAYS[cfg.day]}</b> at <b>{String(cfg.hour).padStart(2, "0")}:{String(cfg.minute).padStart(2, "0")} IST</b>. (Backend cron: Monday 9:00 AM IST by default.)</p>

        <div className="mb-3">
          <label className="text-xs font-semibold text-ink-600">Audience</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {AUD.map(([k, l]) => <button key={k} onClick={() => toggleAud(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${cfg.audiences.includes(k) ? "bg-coral text-white border-coral" : "bg-white border-border"}`} data-testid={`weekly-aud-${k}`}>{l}</button>)}
          </div>
        </div>

        <div className="mb-3"><label className="text-xs font-semibold text-ink-600">Headline</label><input value={cfg.headline || ""} onChange={(e) => set("headline", e.target.value)} className="w-full mt-1 bg-surface rounded-lg px-3 py-2 text-sm" data-testid="weekly-headline" /></div>
        <div className="mb-3"><label className="text-xs font-semibold text-ink-600">Custom note (optional)</label><textarea value={cfg.custom_note || ""} onChange={(e) => set("custom_note", e.target.value)} rows={2} className="w-full mt-1 bg-surface rounded-lg px-3 py-2 text-sm" /></div>

        <div className="flex gap-2">
          <button onClick={save} disabled={saving} className="flex-1 bg-ink text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm" data-testid="weekly-save">{saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save Settings</button>
          <button onClick={triggerNow} disabled={triggering} className="flex-1 bg-coral text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm" data-testid="weekly-trigger">{triggering ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Send Now (manual)</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-border p-4 mt-3">
        <h3 className="font-bold text-ink mb-2">Recent runs</h3>
        {runs.length ? runs.map((r) => (
          <div key={r.run_id} className="flex items-center justify-between border-b border-border last:border-0 py-2 text-sm" data-testid={`weekly-run-${r.run_id}`}>
            <div><div className="font-semibold">{r.run_id}</div><div className="text-xs text-ink-600">{(r.started_at || "").slice(0, 16).replace("T", " ")}</div></div>
            <div className="text-right text-xs">
              <span className={`font-semibold ${r.status === "completed" ? "text-emerald-600" : "text-amber-600"}`}>{r.status}</span>
              {r.counts && <div className="text-ink-600">✉ {r.counts.email_sent || 0} sent · {r.counts.email_failed || 0} failed · 🔔 {r.counts.inapp || 0}</div>}
            </div>
          </div>
        )) : <p className="text-sm text-ink-600">No runs yet.</p>}
      </div>
    </div>
  );
}
